import React, { useState, useEffect, useRef } from 'react'
import { Outlet, useNavigate, Link, useLocation, useParams } from 'react-router-dom'
import {
  Palette,
  LogOut,
  Shield,
  Bell,
  CheckCircle2,
  User,
  Building2,
  ExternalLink,
} from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { usePermissionContext } from '../../contexts/PermissionContext'
import { useOrgBranding } from '../../hooks/useOrgBranding'
import { Logo } from '../ui/Logo'
import { ThemeToggle } from '../ui/ThemeToggle'
import toast from 'react-hot-toast'

export const OrgAdminLayout: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { orgId } = useParams<{ orgId: string }>()
  const { logout, user } = useAuth()
  const { isPlatformAdmin } = usePermissionContext()
  const { branding } = useOrgBranding(orgId)
  const [profileMenuOpen, setProfileMenuOpen] = useState(false)
  const profileRef = useRef<HTMLDivElement>(null)

  const orgName = branding?.orgName ?? 'Organization'

  const handleLogout = () => {
    logout()
    navigate('/')
    toast.success('Logged out successfully')
  }

  const getInitials = () => {
    if (!user) return 'OA'
    const firstInitial = user.firstName?.[0] || ''
    const lastInitial = user.lastName?.[0] || ''
    const initials = (firstInitial + lastInitial).toUpperCase()
    return initials || user.email?.[0]?.toUpperCase() || 'OA'
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setProfileMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const orgNavItems = [
    {
      path: `/app/org/${orgId}/admin/branding`,
      label: 'Branding',
      icon: Palette,
    },
  ]

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 relative overflow-hidden flex flex-col transition-colors duration-300">
      <div className="px-4 sm:px-6 lg:px-8 pt-8 sticky top-0 z-50 flex-shrink-0">
        <nav className="max-w-7xl mx-auto bg-white/60 dark:bg-slate-800/60 backdrop-blur-3xl rounded-[28px] shadow-[0_8px_32px_rgba(0,0,0,0.08)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.3)] border border-teal-200/60 dark:border-teal-700/40 ring-1 ring-teal-500/10 dark:ring-teal-400/10 overflow-visible">
          <div className="px-4 md:px-6 overflow-visible rounded-[28px]">
            <div className="flex items-center h-20 gap-3 md:gap-4 lg:gap-6">
              <Link
                to="/app"
                className="flex items-center space-x-2 flex-shrink-0 group mr-2 md:mr-4"
              >
                <Logo className="h-10 w-auto transition-transform group-hover:scale-105" />
              </Link>

              <div className="flex items-center gap-2 flex-shrink-0">
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-teal-50 dark:bg-teal-900/30 border border-teal-200 dark:border-teal-700/50">
                  <Building2 className="h-3.5 w-3.5 text-teal-600 dark:text-teal-400" />
                  <span className="text-xs font-semibold text-teal-700 dark:text-teal-300">
                    Org Admin: {orgName}
                  </span>
                </div>
                {isPlatformAdmin && (
                  <div
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl bg-amber-50 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-600/50"
                    aria-label="Acting as platform admin"
                  >
                    <Shield className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
                    <span className="text-xs font-semibold text-amber-700 dark:text-amber-300 hidden sm:inline">
                      Acting as platform admin
                    </span>
                  </div>
                )}
                {isPlatformAdmin && (
                  <Link
                    to="/app/admin"
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-xs font-medium text-slate-600 dark:text-slate-400 hover:text-[#69adba] dark:hover:text-primary-400 hover:bg-white/40 dark:hover:bg-slate-700/40 transition-all duration-200"
                    title="Switch to Platform Admin"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Platform Admin</span>
                  </Link>
                )}
              </div>

              <div
                className="flex items-center gap-1 md:gap-2 flex-1 justify-center px-2 md:px-4 min-w-0 mr-2 md:mr-4"
                style={{ flexWrap: 'nowrap' }}
              >
                <div className="flex items-center gap-1 md:gap-2 justify-center overflow-x-auto w-full">
                  {orgNavItems.map((item) => {
                    const IconComponent = item.icon
                    const active = location.pathname.startsWith(item.path)
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`group relative flex items-center gap-1.5 md:gap-2.5 px-2 md:px-3 lg:px-4 py-2 md:py-3 rounded-2xl transition-all duration-300 outline-none focus:outline-none whitespace-nowrap ${
                          active
                            ? 'text-teal-600 dark:text-teal-400 font-bold underline decoration-2 underline-offset-4'
                            : 'text-slate-700 dark:text-slate-300 hover:text-teal-600 dark:hover:text-teal-400 font-medium'
                        }`}
                        style={{ flexShrink: 0, position: 'relative', zIndex: 1 }}
                        title={item.label}
                      >
                        {!active && (
                          <div className="absolute inset-0 bg-white/40 dark:bg-slate-700/40 backdrop-blur-xl rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        )}
                        <IconComponent
                          className={`h-5 w-5 relative z-10 flex-shrink-0 ${
                            active ? 'text-teal-600 dark:text-teal-400' : 'text-slate-600 dark:text-slate-400'
                          }`}
                          style={{ display: 'block' }}
                        />
                        <span className={`text-sm relative z-10 ${active ? 'text-teal-600 dark:text-teal-400 font-bold' : ''}`}>
                          {item.label}
                        </span>
                      </Link>
                    )
                  })}
                </div>
              </div>

              <div className="flex items-center gap-2 md:gap-3 flex-shrink-0 min-w-fit">
                <ThemeToggle />

                <div
                  className="relative"
                  ref={profileRef}
                  style={{ zIndex: 10001 }}
                >
                  <button
                    onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                    className="group relative flex items-center px-2 lg:px-3 py-2 rounded-2xl transition-all duration-300 hover:bg-white/40 dark:hover:bg-slate-700/40 hover:backdrop-blur-xl"
                  >
                    <div className="w-9 h-9 lg:w-11 lg:h-11 rounded-full bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800 dark:from-teal-500 dark:via-teal-600 dark:to-teal-700 flex items-center justify-center text-white font-bold text-xs lg:text-sm shadow-xl ring-2 lg:ring-4 ring-white/50 dark:ring-slate-700/50 group-hover:ring-white/70 dark:group-hover:ring-slate-600/70 group-hover:shadow-2xl transition-all duration-300">
                      {getInitials()}
                    </div>
                  </button>

                  {profileMenuOpen && (
                    <div className="absolute right-0 top-full mt-3 w-64 z-[10000] animate-in fade-in slide-in-from-top-2 duration-200">
                      <div className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-3xl rounded-[24px] shadow-[0_12px_40px_rgba(0,0,0,0.12),0_4px_12px_rgba(0,0,0,0.06)] dark:shadow-[0_12px_40px_rgba(0,0,0,0.4),0_4px_12px_rgba(0,0,0,0.2)] border border-white/60 dark:border-slate-700/60 ring-1 ring-black/[0.03] dark:ring-white/[0.03] overflow-hidden">
                        <div className="px-5 py-4 bg-gradient-to-br from-teal-50 dark:from-teal-900/30 to-transparent border-b border-white/50 dark:border-slate-700/50">
                          <div className="flex items-center space-x-3">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800 flex items-center justify-center text-white font-bold shadow-xl">
                              {getInitials()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-bold text-slate-900 dark:text-slate-100 truncate flex items-center gap-2">
                                <User className="h-4 w-4 text-teal-600 dark:text-teal-400" />
                                {user?.firstName} {user?.lastName}
                              </p>
                              <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                                {user?.email}
                              </p>
                              <p className="text-[10px] text-teal-700 dark:text-teal-400 font-semibold mt-0.5 flex items-center gap-1">
                                <Shield className="h-3 w-3" />
                                Org Admin
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="p-2">
                          {isPlatformAdmin && (
                            <Link
                              to="/app/admin"
                              onClick={() => setProfileMenuOpen(false)}
                              className="group flex items-center space-x-3 px-4 py-3 rounded-2xl text-slate-700 dark:text-slate-200 hover:bg-white/60 dark:hover:bg-slate-700/60 hover:backdrop-blur-xl transition-all duration-200"
                            >
                              <div className="w-8 h-8 rounded-xl bg-slate-100 dark:bg-slate-700/50 flex items-center justify-center group-hover:bg-slate-200 dark:group-hover:bg-slate-600/50 transition-colors">
                                <Bell className="h-4 w-4 text-slate-600 dark:text-slate-400" />
                              </div>
                              <span className="font-medium">Platform Admin</span>
                            </Link>
                          )}

                          <Link
                            to="/app/profile"
                            onClick={() => setProfileMenuOpen(false)}
                            className="group flex items-center space-x-3 px-4 py-3 rounded-2xl text-slate-700 dark:text-slate-200 hover:bg-white/60 dark:hover:bg-slate-700/60 hover:backdrop-blur-xl transition-all duration-200"
                          >
                            <div className="w-8 h-8 rounded-xl bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center group-hover:bg-teal-200 dark:group-hover:bg-teal-800/40 transition-colors">
                              <User className="h-4 w-4 text-teal-600 dark:text-teal-400" />
                            </div>
                            <span className="font-medium">Profile Settings</span>
                          </Link>

                          <button
                            onClick={() => {
                              setProfileMenuOpen(false)
                              handleLogout()
                            }}
                            className="group flex items-center space-x-3 px-4 py-3 rounded-2xl text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition-all duration-200 w-full mt-2"
                          >
                            <div className="w-8 h-8 rounded-xl bg-red-100 dark:bg-red-900/50 flex items-center justify-center group-hover:bg-red-200 dark:group-hover:bg-red-800/50 transition-colors">
                              <LogOut className="h-4 w-4 text-red-600 dark:text-red-400" />
                            </div>
                            <span className="font-medium">Logout</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </nav>
      </div>

      <div className="px-4 sm:px-6 lg:px-8 py-8 relative z-10 flex-grow">
        <main className="max-w-7xl mx-auto">
          <Outlet />
        </main>
      </div>

      <footer className="px-4 sm:px-6 lg:px-8 pb-8 relative z-0 mb-4 mt-auto flex-shrink-0">
        <div className="max-w-7xl mx-auto bg-white/60 dark:bg-slate-800/60 backdrop-blur-3xl rounded-[28px] shadow-[0_8px_32px_rgba(0,0,0,0.08)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.3)] border border-white/50 dark:border-slate-700/50 ring-1 ring-black/[0.03] dark:ring-white/[0.03] px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex flex-col md:flex-row items-center gap-4">
              <Logo className="h-10 w-auto" />
              <div className="text-center md:text-left">
                <p className="text-slate-900 dark:text-slate-100 text-sm font-bold">
                  Open1031 — {orgName} Admin
                </p>
                <p className="text-slate-600 dark:text-slate-400 text-xs mt-1">
                  Copyright &copy; {new Date().getFullYear()} All Rights Reserved.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-teal-600 dark:text-teal-400 font-medium">
              <CheckCircle2 className="h-4 w-4" />
              Org-scoped admin view
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
